import { cn } from '@/lib/cn';
import type { Card } from '@/lib/qcp/protocol';
import { PlayingCard } from './PlayingCard';

interface OfcRowProps {
  label: 'TOP' | 'MID' | 'BOT';
  cards: Card[];
  capacity: 3 | 5;
  /** Called with the slot index when a slot (filled or empty) is tapped. */
  onSlotTap?: (index: number) => void;
  /** Highlight this row as a legal placement target (brand ring on the row). */
  highlight?: boolean;
  /** Keys (`${r}${s}`) of cards placed THIS hand — drawn with a dim ring. */
  pendingKeys?: Set<string>;
  /** Key of the currently picked-up pending card — drawn with a bright ring. */
  pickedKey?: string;
}

/**
 * One row of an OFC board — front (TOP/3 slots), middle (MID/5 slots),
 * back (BOT/5 slots).
 *
 * Renders exactly `capacity` slots: filled cards + empty placeholders for
 * unfilled slots. The row label sits to the left. An optional `highlight`
 * ring signals a legal drop target. Each slot fires `onSlotTap(index)`.
 */
export function OfcRow({ label, cards, capacity, onSlotTap, highlight, pendingKeys, pickedKey }: OfcRowProps) {
  const slots = Array.from({ length: capacity }, (_, i) => cards[i] ?? null);

  // Map label to a colour accent for the row badge.
  const labelColour: Record<OfcRowProps['label'], string> = {
    TOP: 'text-yellow-400',
    MID: 'text-brand',
    BOT: 'text-emerald-400',
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg p-1.5',
        highlight && 'ring-2 ring-brand bg-brand/5',
      )}
    >
      {/* Row label */}
      <span
        className={cn(
          'w-7 shrink-0 text-center text-[10px] font-bold uppercase tracking-widest',
          labelColour[label],
        )}
      >
        {label}
      </span>

      {/* Card slots */}
      <div className="flex gap-1">
        {slots.map((card, idx) => {
          const key = card ? `${card.r}${card.s}` : null;
          const isPending = key ? (pendingKeys?.has(key) ?? false) : false;
          const isPicked = key !== null && key === pickedKey;
          return (
            <PlayingCard
              key={idx}
              card={card ?? undefined}
              selected={isPicked}
              pending={isPending && !isPicked}
              onClick={onSlotTap ? () => onSlotTap(idx) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

